import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { isUnauthorizedError } from '@/lib/authUtils';
import { apiRequest } from '@/lib/queryClient';
import type { Task, TimeEntryWithUser } from '@shared/schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

interface TimeTrackerProps {
  task: Task;
  canTrackTime: boolean;
}

export default function TimeTracker({ task, canTrackTime }: TimeTrackerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [stopDescription, setStopDescription] = useState('');
  const [localStartTime, setLocalStartTime] = useState<Date | null>(null);

  // Initialize timer state when component mounts or task changes
  useEffect(() => {
    const isRunning = !!task.timeStarted;
    setIsTimerRunning(isRunning);

    // If timer is running, calculate initial elapsed time
    if (isRunning && task.timeStarted) {
      const startTime = new Date(task.timeStarted).getTime();
      const now = new Date().getTime();
      const elapsed = Math.floor((now - startTime) / 1000);
      setElapsedTime(elapsed);
      setLocalStartTime(new Date(task.timeStarted));
    } else {
      setElapsedTime(0);
      setLocalStartTime(null);
    }
  }, [task.id, task.timeStarted]); // Include task.id to reinitialize when task changes

  // Fetch time entries for this task
  const { data: timeEntries = [] } = useQuery<TimeEntryWithUser[]>({
    queryKey: [`/api/tasks/${task.id}/time-entries`],
    enabled: !!task.id,
  });

  const startTimerMutation = useMutation({
    mutationFn: async () => {
      // Double-check timer state before making API call
      if (isTimerRunning || task.timeStarted) {
        throw new Error('Timer is already running for this task');
      }
      await apiRequest('POST', `/api/tasks/${task.id}/start-timer`);
    },
    onSuccess: async () => {
      const startTime = new Date();
      setIsTimerRunning(true);
      setElapsedTime(0); // Reset elapsed time when starting
      setLocalStartTime(startTime);
      await queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      toast({
        title: 'Timer Started',
        description: `Started tracking time for "${task.title}"`,
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }

      // Handle timer already running error gracefully
      if (error.message?.includes('Timer is already running')) {
        setIsTimerRunning(true);
        queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
        toast({
          title: 'Timer Already Running',
          description: 'Timer was already started for this task',
        });
        return;
      }

      toast({
        title: 'Error',
        description: 'Failed to start timer',
        variant: 'destructive',
      });
    },
  });

  const stopTimerMutation = useMutation({
    mutationFn: async (description: string) => {
      await apiRequest('POST', `/api/tasks/${task.id}/stop-timer`, {
        description,
      });
    },
    onSuccess: () => {
      setIsTimerRunning(false);
      setElapsedTime(0);
      setStopDescription('');
      setLocalStartTime(null);
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
      queryClient.invalidateQueries({
        queryKey: [`/api/tasks/${task.id}/time-entries`],
      });
      toast({
        title: 'Timer Stopped',
        description: 'Time entry saved successfully',
      });
    },
    onError: error => {
      if (isUnauthorizedError(error)) {
        toast({
          title: 'Unauthorized',
          description: 'You are logged out. Logging in again...',
          variant: 'destructive',
        });
        setTimeout(() => {
          window.location.href = '/api/login';
        }, 500);
        return;
      }
      toast({
        title: 'Error',
        description: 'Failed to stop timer',
        variant: 'destructive',
      });
    },
  });

  // Update elapsed time every second when timer is running
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isTimerRunning && localStartTime) {
      interval = setInterval(() => {
        const startTime = localStartTime.getTime();
        const now = new Date().getTime();
        const elapsed = Math.floor((now - startTime) / 1000);

        // Ensure elapsed time is not negative
        if (elapsed >= 0) {
          setElapsedTime(elapsed);
        }
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isTimerRunning, localStartTime]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const totalTimeTracked = task.timeTracked || 0;
  const estimatedHours = task.estimatedHours || 0;
  const progressPercent =
    estimatedHours > 0
      ? Math.min(100, (totalTimeTracked / (estimatedHours * 60)) * 100)
      : 0;

  return (
    <Card className="w-full border-2 border-blue-100 dark:border-blue-900/20 shadow-lg">
      {/* <CardHeader className="pb-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10">
        <CardTitle className="flex items-center justify-between text-xl">
          <span className="flex items-center text-blue-800 dark:text-blue-200">
            <Timer className="w-6 h-6 mr-3 text-blue-600" />
            Time Tracker
          </span>
          {canTrackTime && (
            <div className="flex items-center space-x-2">
              {!isTimerRunning ? (
                <Button
                  onClick={() => startTimerMutation.mutate()}
                  disabled={startTimerMutation.isPending}
                  size="sm"
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg transition-all duration-200 transform hover:scale-105"
                  data-testid="button-start-timer"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Timer
                </Button>
              ) : (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      size="sm"
                      className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white shadow-lg transition-all duration-200 transform hover:scale-105"
                      data-testid="button-stop-timer"
                    >
                      <Square className="w-4 h-4 mr-2" />
                      Stop Timer
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center text-lg">
                        <Pause className="w-5 h-5 mr-2 text-red-500" />
                        Stop Timer
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                        <div className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-1">
                          Session Time
                        </div>
                        <div className="text-2xl font-mono text-blue-900 dark:text-blue-100">
                          {formatTime(elapsedTime)}
                        </div>
                      </div>
                      <div>
                        <Label
                          htmlFor="description"
                          className="text-sm font-medium"
                        >
                          Work Description (Optional)
                        </Label>
                        <Textarea
                          id="description"
                          placeholder="What did you work on during this session?"
                          value={stopDescription}
                          onChange={e => setStopDescription(e.target.value)}
                          data-testid="textarea-stop-description"
                          className="mt-2"
                        />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Button
                          onClick={() =>
                            stopTimerMutation.mutate(stopDescription)
                          }
                          disabled={stopTimerMutation.isPending}
                          data-testid="button-confirm-stop-timer"
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {stopTimerMutation.isPending ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Stopping...
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Stop Timer
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6 p-6">

        {isTimerRunning && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800 rounded-xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mr-3"></div>
                <span className="text-green-800 dark:text-green-200 font-semibold text-lg">
                  Timer Active
                </span>
              </div>
              <Badge
                variant="secondary"
                className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
              >
                <Zap className="w-3 h-3 mr-1" />
                Running
              </Badge>
            </div>
            <div className="text-center">
              <div className="text-4xl font-mono font-bold text-green-800 dark:text-green-200 mb-2">
                {formatTime(elapsedTime)}
              </div>
              <div className="text-sm text-green-600 dark:text-green-400">
                Session in progress
              </div>
            </div>
          </div>
        )}


        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center mb-2">
              <Clock className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                Total Tracked
              </span>
            </div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {formatDuration(totalTimeTracked)}
            </div>
            <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
              All time entries
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/10 dark:to-pink-900/10 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center mb-2">
              <Target className="w-5 h-5 text-purple-600 mr-2" />
              <span className="text-sm font-medium text-purple-800 dark:text-purple-200">
                Estimated
              </span>
            </div>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {estimatedHours > 0 ? `${estimatedHours}h` : 'N/A'}
            </div>
            <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
              Target time
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/10 dark:to-red-900/10 rounded-xl p-4 border border-orange-200 dark:border-orange-800">
            <div className="flex items-center mb-2">
              <TrendingUp className="w-5 h-5 text-orange-600 mr-2" />
              <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
                Sessions
              </span>
            </div>
            <div className="text-2xl font-bold text-orange-900 dark:text-orange-100">
              {timeEntries.length}
            </div>
            <div className="text-xs text-orange-600 dark:text-orange-400 mt-1">
              Time entries
            </div>
          </div>
        </div>


        {estimatedHours > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Progress
              </span>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {Math.round(progressPercent)}%
                </span>
                {progressPercent >= 100 && (
                  <AlertCircle className="w-4 h-4 text-red-500" />
                )}
              </div>
            </div>
            <Progress value={Math.min(100, progressPercent)} className="h-3" />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>{formatDuration(totalTimeTracked)} tracked</span>
              <span>{estimatedHours}h estimated</span>
            </div>
          </div>
        )}


        {timeEntries.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                <History className="w-4 h-4 mr-2 text-gray-600" />
                Recent Sessions
              </h4>
              <Badge variant="outline" className="text-xs">
                {timeEntries.length} entries
              </Badge>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {timeEntries.slice(0, 5).map((entry, index) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-3">
                      <span className="text-xs font-semibold text-blue-800 dark:text-blue-200">
                        {index + 1}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {formatDuration(entry.duration || 0)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {entry.startTime
                          ? (() => {
                              const startDate = new Date(entry.startTime);
                              return isNaN(startDate.getTime()) ? (
                                'Invalid date'
                              ) : (
                                <>
                                  {startDate.toLocaleDateString()} at{' '}
                                  {startDate.toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </>
                              );
                            })()
                          : 'No start time recorded'}
                      </div>
                    </div>
                  </div>
                  {entry.description && (
                    <div className="text-xs text-gray-600 dark:text-gray-300 max-w-32 truncate bg-white dark:bg-gray-700 px-2 py-1 rounded">
                      {entry.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}


        {!isTimerRunning && canTrackTime && (
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
              Ready to track your time? Click "Start Timer" to begin your work
              session.
            </div>
          </div>
        )}
      </CardContent> */}
    </Card>
  );
}
